import { useEffect, useState, useCallback } from "react";

// Firebase messaging is initialized from the existing firebaseConfig.js
// Import it conditionally to avoid breaking builds if messaging isn't enabled
let messagingPromise = null;

const getMessaging = async () => {
  if (messagingPromise) return messagingPromise;
  messagingPromise = (async () => {
    try {
      const { getApp } = await import("firebase/app");
      const { getMessaging, getToken, onMessage } = await import("firebase/messaging");
      const app = getApp();
      const messaging = getMessaging(app);
      return { messaging, getToken, onMessage };
    } catch (e) {
      console.warn("Firebase messaging not available:", e.message);
      return null;
    }
  })();
  return messagingPromise;
};

// VAPID key — set this in your Firebase console under Cloud Messaging settings
// Replace with your actual VAPID public key from Firebase Project Settings > Cloud Messaging
const VAPID_KEY = process.env.REACT_APP_VAPID_KEY || "";

export function usePushNotifications() {
  const [permission, setPermission] = useState(Notification.permission);
  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    const supported =
      "Notification" in window &&
      "serviceWorker" in navigator &&
      "PushManager" in window;
    setIsSupported(supported);
  }, []);

  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      setError("Push notifications are not supported in this browser.");
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result !== "granted") {
        setError("Notification permission denied.");
        return false;
      }

      // Register service worker
      const registration = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js"
      );

      // Send firebase config to service worker
      const { getApp } = await import("firebase/app");
      const app = getApp();
      if (registration.active) {
        registration.active.postMessage({
          type: "FIREBASE_CONFIG",
          config: app.options,
        });
      }

      // Get FCM token
      const m = await getMessaging();
      if (!m) throw new Error("Firebase messaging not initialized");

      const fcmToken = await m.getToken(m.messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      if (fcmToken) {
        setToken(fcmToken);
        // Store token for backend use
        localStorage.setItem("fcmToken", fcmToken);
        // Save to Firestore via the existing pattern in the app
        await saveTokenToFirestore(fcmToken);
        return true;
      } else {
        setError("No FCM token returned. Check VAPID key configuration.");
        return false;
      }
    } catch (err) {
      console.error("Push notification setup error:", err);
      setError(err.message);
      return false;
    }
  }, [isSupported]);

  return { permission, token, error, isSupported, requestPermission };
}

// Save FCM token to Firestore so backend can target this device
async function saveTokenToFirestore(fcmToken) {
  try {
    const { getApp } = await import("firebase/app");
    const { getFirestore, doc, setDoc, getAuth } = await import("firebase/firestore");
    const { getAuth: fbGetAuth } = await import("firebase/auth");
    const app = getApp();
    const db = getFirestore(app);
    const auth = fbGetAuth(app);
    const user = auth.currentUser;
    if (!user) return;
    await setDoc(
      doc(db, "users", user.uid, "tokens", "fcm"),
      {
        token: fcmToken,
        updatedAt: new Date().toISOString(),
        platform: "web",
      },
      { merge: true }
    );
  } catch (e) {
    console.warn("Failed to save FCM token to Firestore:", e.message);
  }
}
